/**
 * Module for handling digital product variant display logic
 * Merged version incorporating comprehensive functionality with clean architecture
 */

/**
 * Extracts variant ID from form input
 * @returns {string|null} The variant ID or null if not found
 */
const getVariantFromForm = () => {
  const input = document.querySelector(".product-form input[name='id']");
  if (input && input.value) {
    return input.value;
  }
  return null;
};

/**
 * Extracts variant ID from URL parameters
 * @param {string} url - The URL to parse
 * @returns {string|null} The variant ID or null if not found
 */
const getVariantFromUrl = (url) => {
  try {
    const urlParams = new URLSearchParams(new URL(url).search);
    return urlParams.get('variant');
  } catch (error) {
    console.error('Error parsing URL:', error);
    return null;
  }
};

/**
 * Gets the current variant from form or URL
 * @returns {string|null} The current variant ID
 */
const getCurrentVariant = () => {
  return getVariantFromForm() || getVariantFromUrl(window.location.href);
};

/**
 * Updates URL with variant parameter
 * @param {string} variantId - The variant ID to add to URL
 */
const updateUrlWithVariant = (variantId) => {
  if (!variantId) return;
  const newUrl = `${window.location.pathname}?variant=${variantId}`;
  window.history.replaceState({}, '', newUrl);
};

/**
 * Main product variant handler class
 */
class ProductVariantHandler {
  constructor() {
    this.variantData = this.getVariantData();
    this.addToBasketForm = document.querySelector('.product-form');
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    this.init();
  }

  /**
   * Get variant data from DOM
   * @returns {Object} Parsed variant data
   */
  getVariantData() {
    try {
      const variantElement = document.getElementById('variant-data');
      if (!variantElement) {
        throw new Error('Variant data element not found');
      }
      return JSON.parse(variantElement.textContent);
    } catch (error) {
      console.error('Error parsing variant data:', error);
      return { digital_variants: [] };
    }
  }

  /**
   * Update digital extension logic based on current variant
   */
  updateDigitalExtensionLogic() {
    const currentVariant = getCurrentVariant();
    const digitalDownloadForm = document.querySelector('#digital-download-form');
    const url = new URL(window.location.href);
    const hasCorrectVariantParam = url.searchParams.get('variant') === currentVariant;

    if (
      this.variantData &&
      this.variantData.digital_variants &&
      this.variantData.digital_variants.includes(currentVariant)
    ) {
      // Show digital download form for digital variants
      if (this.addToBasketForm) {
        this.addToBasketForm.style.display = 'none';
      }
      if (digitalDownloadForm) {
        digitalDownloadForm.style.display = 'block';
      }
    } else {
      // Show regular add to basket form for non-digital variants
      if (this.addToBasketForm) {
        this.addToBasketForm.style.display = 'block';
      }
      if (digitalDownloadForm) {
        digitalDownloadForm.style.display = 'none';
      }
    }

    if (!hasCorrectVariantParam && currentVariant) {
      updateUrlWithVariant(currentVariant);
    }
  }

  /**
   * Handle URL changes and update UI accordingly
   */
  handleUrlChange = () => {
    const currentUrl = window.location.href;
    const currentVariant = getVariantFromUrl(currentUrl);

    if (!currentVariant) {
      return;
    }

    this.updateDigitalExtensionLogic();

    if (currentVariant !== this.initialVariant) {
      // Restore original history methods before redirect
      this.restoreHistoryMethods();
      window.location.href = currentUrl;
    }
  };

  /**
   * Override history methods to detect URL changes
   */
  overrideHistoryMethods() {
    history.pushState = (...args) => {
      this.originalPushState.apply(history, args);
      this.handleUrlChange();
    };

    history.replaceState = (...args) => {
      this.originalReplaceState.apply(history, args);
      this.handleUrlChange();
    };
  }

  /**
   * Restore original history methods
   */
  restoreHistoryMethods() {
    history.pushState = this.originalPushState;
    history.replaceState = this.originalReplaceState;
  }

  /**
   * Set up MutationObserver for variant input changes
   */
  setupVariantObserver() {
    const input = document.querySelector(".product-form input[name='id']");
    if (input) {
      let lastValue = input.value;
      const observer = new MutationObserver(() => {
        if (input.value !== lastValue) {
          lastValue = input.value;
          this.updateDigitalExtensionLogic();
        }
      });
      observer.observe(input, { attributes: true, attributeFilter: ['value'] });
      input.addEventListener('change', () => {
        if (input.value !== lastValue) {
          lastValue = input.value;
          this.updateDigitalExtensionLogic();
        }
      });
    }
  }

  /**
   * Set up observer for digital form parent
   */
  setupDigitalFormObserver() {
    const digitalFormParent = document.getElementById(
      'shopify-block-AOXJsSDRCYnVpVlR4a__odbm_store_app_request_for_content_XBw7QP'
    );
    if (digitalFormParent) {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node.id === 'digital-download-form') {
                this.updateDigitalExtensionLogic();
              }
            });
          }
        }
      });
      observer.observe(digitalFormParent, { childList: true, subtree: true });

      const immediateForm = document.getElementById('digital-download-form');
      if (immediateForm) {
        this.updateDigitalExtensionLogic();
      } else {
        let pollCount = 0;
        const maxPolls = 20;
        const pollInterval = setInterval(() => {
          const polledForm = document.getElementById('digital-download-form');
          if (polledForm) {
            this.updateDigitalExtensionLogic();
            clearInterval(pollInterval);
          } else if (++pollCount >= maxPolls) {
            clearInterval(pollInterval);
          }
        }, 100);
      }
    }
  }

  /**
   * Set up event listeners for variant type inputs
   */
  setupVariantTypeListeners() {
    document.querySelectorAll("input[name='Type-1']").forEach((input) => {
      input.addEventListener('change', () => {
        setTimeout(() => {
          this.updateDigitalExtensionLogic();
        }, 50);
      });
    });
  }

  /**
   * Set up pub/sub event listener
   */
  setupPubSubListener() {
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
        this.updateDigitalExtensionLogic();
      });
    }
  }

  /**
   * Initialize event listeners and observers
   */
  init() {
    this.overrideHistoryMethods();
    this.setupVariantObserver();
    this.setupDigitalFormObserver();
    this.setupVariantTypeListeners();
    this.setupPubSubListener();

    // Run on page load
    this.updateDigitalExtensionLogic();

    // Clean up on page unload
    window.addEventListener('unload', () => {
      this.restoreHistoryMethods();
    });
  }
}

// Initialize the handler when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductVariantHandler();
});
